import { useMemo } from 'react'
import { Organization } from '../../../types'
import { useData } from '../contexts/DataContextProvider'
import styles from './Multiples.module.scss'
import MultiplesHeader from './MultiplesHeader'
import MultipleDotBars from './MultipleDotBars'
import { DEFAULT_LEGEND_ITEMS } from '../constants'

export default function LiableInstitutions() {
  const { data, definitions } = useData()

  const dataByGovernmentTarget = useMemo(() => {
    const targetTypes = new Map<
      string,
      { name: string; orgs: Organization[] }
    >()
    definitions.forEach(({ id = '', display_label: name }) => {
      if (id.includes('government:')) {
        const type =
          id
            ?.split(':')
            .filter((value) => value !== 'target')
            .join(':') || ''
        targetTypes.set(type, { name, orgs: [] })
      }
    })

    data.forEach((org) => {
      org?.target?.forEach((type) => {
        if (targetTypes.has(type)) {
          targetTypes.get(type)?.orgs.push(org)
        }
      })
    })

    return Array.from(targetTypes.entries())
      .map(([id, { name, orgs }]) => ({
        name,
        orgs,
        value: `${((orgs.length / data.length) * 100).toFixed(1)}%`, // Calculate percentage of total organizations
      }))
      .sort(({ orgs: a }, { orgs: b }) => b.length - a.length)
  }, [data, definitions])

  const dataByInstitutionTarget = useMemo(() => {
    const institutionTypes = new Map<
      string,
      { name: string; orgs: Organization[] }
    >()
    definitions.forEach(({ id = '', display_label: name }) => {
      if (id.includes('target:institutions:')) {
        const type =
          id
            ?.split(':')
            .filter((value) => value !== 'target')
            .join(':') || ''
        institutionTypes.set(type, { name, orgs: [] })
      }
    })

    data.forEach((org) => {
      org?.target?.forEach((type) => {
        if (institutionTypes.has(type)) {
          institutionTypes.get(type)?.orgs.push(org)
        }
      })
    })

    return Array.from(institutionTypes.entries())
      .map(([id, { name, orgs }]) => ({
        name,
        orgs,
        value: `${((orgs.length / data.length) * 100).toFixed(1)}%`, // Calculate percentage of total organizations
      }))
      .sort(({ orgs: a }, { orgs: b }) => b.length - a.length)
  }, [data, definitions])

  return (
    <div className={`${styles.layout} ${styles.dark}`}>
      <MultiplesHeader
        title="Liable Institutions"
        copy={[
          'Reparations actions may be directed towards various entities including the federal government, state or local governments, or private establishments. We define a liable institution as an organization that might bear responsibility or be held accountable for past injustices such as slavery, segregation, and racial discrimination.',

          'Likewise, private sectors, like media, healthcare, finance, and others, have been identified as liable organizations given their participation in chattel slavery and other systemic harms against Black people in America.',

          'You can see in the chart below the percentage of organizations that identify as targeting these liable institutions.',
        ]}
      />

      <MultipleDotBars
        groupTitle="Government"
        maxNumberOfData={data.length}
        dataByMultiple={dataByGovernmentTarget}
        gridTemplateColumns="repeat(auto-fit, 200px)"
      />

      <MultipleDotBars
        groupTitle="Institutions"
        maxNumberOfData={data.length}
        dataByMultiple={dataByInstitutionTarget}
        gridTemplateColumns="repeat(auto-fit, 200px)"
        legendItems={DEFAULT_LEGEND_ITEMS}
      />
    </div>
  )
}
